import React, { useEffect } from "react";
import { Modal, Table, Button, Icon, Loader, Message } from "semantic-ui-react";
import { useGetMetricPermits } from "../operations/queries/getMetricPermits";
import { PermitMetricScopeInput } from "../operations/queries/getPermitsCountByStatesMetric";

interface Props {
  open: boolean;
  title: string;
  scope: PermitMetricScopeInput;
  onClose: () => void;
}

/**
 * Org-level drill-through: lists the permit records backing the current metric
 * scope (including any selected state slice via scope.stateKeys). Works at any
 * level because it reuses the same metric scope as the aggregates.
 */
const DrillThroughPermitsModal = ({ open, title, scope, onClose }: Props) => {
  const { getMetricPermits, data, status } = useGetMetricPermits();

  useEffect(() => {
    if (open && scope.organizationId) {
      getMetricPermits({
        variables: { query: { scope }, skip: 0, take: 50 },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scope]);

  const result = data?.metricPermits;

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name="list" /> {title}
      </Modal.Header>
      <Modal.Content scrolling>
        {status.loading && <Loader active inline="centered" content="Loading permits…" />}
        {!status.loading && status.errors && status.errors.length > 0 && (
          <Message negative content={status.errors.join(" ")} />
        )}
        {!status.loading && result && result.items.length === 0 && (
          <Message info content="No permits match the current selection." />
        )}
        {!status.loading && result && result.items.length > 0 && (
          <Table celled selectable compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Permit #</Table.HeaderCell>
                <Table.HeaderCell>State</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Project</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {result.items.map((p) => (
                <Table.Row key={p.id}>
                  <Table.Cell>{p.number}</Table.Cell>
                  <Table.Cell>{p.state ?? "—"}</Table.Cell>
                  <Table.Cell>{p.permitTypeName ?? "—"}</Table.Cell>
                  <Table.Cell>{p.projectName ?? "—"}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Modal.Content>
      <Modal.Actions>
        {result && (
          <span style={{ float: "left", lineHeight: "36px", color: "rgba(0,0,0,.6)" }}>
            Showing {result.items.length} of {result.totalCount}
          </span>
        )}
        <Button onClick={onClose}>Close</Button>
      </Modal.Actions>
    </Modal>
  );
};

export default DrillThroughPermitsModal;
